import clsx from "clsx";
import React from "react";
import { Paper, Typography, Link, Box } from "@mui/material";
import makeStyles from "@mui/styles/makeStyles";

import {
  FullScreenDialog,
  FullScreenDialogProps,
} from "../dialog/FullScreenDialog";
import { AppLogo } from "../app/AppLogo";
import newsletter from "../../icons/newsletter.svg";
import { colorSystem } from "../../theme";
import { useGenericErrorHandler } from "../../hooks/useGenericErrorHandler";
import { useToastAlert } from "../app/ToastAlert/ToastAlertProvider";

import { useCurrentBrand } from "../../hooks/useCurrentWorkspace";
import { useMutation } from "@tanstack/react-query";
import SignUpService from "../../services/SignUpService";

const useStyles = makeStyles((theme) => ({
  root: {
    textAlign: "center",

    "& .MuiDialog-paper": {
      backgroundColor: colorSystem.white3,
    },

    "& .MuiContainer-root": {
      maxWidth: 674,
    },
  },

  dialog: {
    padding: theme.spacing(5),
  },

  title: {
    fontSize: 24,
    fontWeight: 600,
    lineHeight: "29px",
    color: theme.palette.text.primary,
    marginTop: theme.spacing(3),
    marginBottom: theme.spacing(5),
  },

  text: {
    fontSize: 16,
    fontWeight: 500,
    lineHeight: "20px",
    color: theme.palette.text.secondary,
    marginTop: theme.spacing(5),
  },

  link: {
    cursor: "pointer",
  },
}));

export interface PasswordResetDialogProps extends FullScreenDialogProps {
  email: string;
}

export function PasswordResetDialog(props: PasswordResetDialogProps) {
  const { className, email, ...other } = props;
  const s = useStyles();
  const { brandName } = useCurrentBrand();
  const onError = useGenericErrorHandler({});
  const { showToastAlert } = useToastAlert();

  const { mutate: resetPassword, isPending: resetPasswordInFlight } =
    useMutation({
      mutationKey: ["reset-password-dialog"],
      mutationFn: SignUpService.resetPassword,
      onSuccess: () => {
        showToastAlert("success", {
          message: "Recovery link sent.",
        });
      },
      onError,
    });

  const handleResendClick = React.useCallback(() => {
    if (!resetPasswordInFlight) {
      resetPassword({ email });
    }
  }, [resetPasswordInFlight, resetPassword, email]);

  return (
    <FullScreenDialog
      className={clsx(s.root, className)}
      fullScreen
      showBackButton={false}
      hideAppToolbar
      {...other}
    >
      <Paper className={s.dialog}>
        <Box
          sx={{
            display: "flex",
            width: "100%",
            justifyContent: "center",
          }}
        >
          <AppLogo main color />
        </Box>
        <Typography className={s.title} variant="h1">
          Your password reset email is on the way!
        </Typography>
        <Box
          sx={{
            display: "flex",
            width: "100%",
            justifyContent: "center",
          }}
        >
          <img src={newsletter} alt="" width={155} />
        </Box>
        <Typography className={s.text} variant="body1">
          If you have a {brandName} account, we'll email you a link to reset
          your password.
        </Typography>

        <Typography className={s.text} variant="body1">
          Can’t find the email? Check your spam folder,
          <br />
          or{" "}
          <Link className={s.link} onClick={handleResendClick}>
            resend the recovery link
          </Link>
          .
        </Typography>
      </Paper>
    </FullScreenDialog>
  );
}
